import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { ContractObjectDefaults } from './contract-object.defaults';
import { AddFactor, CloneFactor, DeleteFactor, RestoreFactor, UpdateFactor } from './factor.actions';
import { AddFactorPointer, RemoveFactorPointer, UpdateLine } from './line.actions';

export interface FactorStateModel {
    factor: any;
    currentID: string;
}

@State<FactorStateModel>({
    name: 'factorState',
    defaults: {
        factor: {},
        currentID: null
    }
})
@Injectable()
export class FactorState {
    constructor(private helper: StateHelperService) { }

    //******* SELECTORS *********

    @Selector()
    static fullState(state: FactorStateModel) {
        return state.factor;
    }

    @Selector()
    static currentFactor(state: FactorStateModel) {
        if (state.factor[state.currentID]) {
            return state.factor[state.currentID];
        } else {
            return {};
        }
    }

    @Selector()
    static factorsByLine(state: FactorStateModel) {
        return (lineID: string) => {
            let result: any = [];
            for (var i in state.factor) {
                if (state.factor[i].Line_ID == lineID) {
                    result.push(state.factor[i]);
                }
            }
            return result;
        };
    }

    @Selector()
    static factorCountByLine(state: FactorStateModel) {
        return (lineID: string) => {
            let count = 0;
            for (var i in state.factor) {
                if (state.factor[i].Line_ID == lineID) {
                    count++;
                }
            }
            return count;
        };
    }

    //******  ACTIONS  ******

    @Action(UpdateFactor)
    updateFactor(sc: StateContext<FactorStateModel>, action: UpdateFactor) {
        const state = sc.getState();
        let factorID = action.Factor.Factor_ID;
        let currentFactor = state.factor[factorID];
        let lineID = currentFactor.Line_ID;
        let updateFactor: any = { ...action.Factor }; //need DEEP copy?
        this.helper.setItemChanged(updateFactor, state.factor[factorID].Edit_Status);

        sc.patchState({
            factor: {
                ...state.factor,
                [factorID]: {
                    ...state.factor[factorID],
                    ...updateFactor
                }
            }
        });
        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(DeleteFactor)
    deleteFactor(sc: StateContext<FactorStateModel>, action: DeleteFactor) {
        const state = sc.getState();
        let factor = { ...state.factor }; //shallow copy Factor from state
        let lineID = factor[action.factorID].Line_ID;
        this.helper.setItemDeleted(factor, action.factorID);

        sc.patchState({ factor: factor });
        if (action.factorID < 0) {
            sc.dispatch(new RemoveFactorPointer(lineID, action.factorID.toString()));
        }
        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(RestoreFactor)
    restoreFactor(sc: StateContext<FactorStateModel>, action: RestoreFactor) {
        const state = sc.getState();
        let factor = { ...state.factor }; //shallow copy Factor from state
        let lineID = factor[action.factorID].Line_ID;

        this.helper.setItemRestored(factor, action.factorID);
        sc.patchState({ factor: factor });

        sc.dispatch(new UpdateLine({ Line_ID: lineID }));
    }

    @Action(CloneFactor)
    cloneFactor(sc: StateContext<FactorStateModel>, action: CloneFactor) {
        const state = sc.getState();
        let srcID = action.factorID;
        let newID = this.helper.getTempID();
        let newFactor = cloneDeep(state.factor[srcID]);
        let lineID = newFactor.Line_ID;
        this.helper.setItemAdded(newFactor);
        newFactor.Factor_ID = newID;
        sc.patchState({
            currentID: newID,
            factor: { ...state.factor, [newID]: newFactor }
        });
        sc.dispatch([new AddFactorPointer(lineID, newID), new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(AddFactor)
    addFactor(sc: StateContext<FactorStateModel>, action: AddFactor) {
        const state = sc.getState();
        let newID = this.helper.getTempID();
        let lineID = action.lineID;
        let newFactor = this.helper.createDefaultItem(ContractObjectDefaults, 'Contract_Factor', action.Factor);
        this.helper.setItemAdded(newFactor);
        newFactor.Factor_ID = newID;
        newFactor.Line_ID = lineID;
        sc.patchState({
            currentID: newID,
            factor: { ...state.factor, [newID]: newFactor }
        });
        sc.dispatch([new AddFactorPointer(lineID, newID), new UpdateLine({ Line_ID: lineID })]);
    }
}
